import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Wallet, TrendingUp, RotateCcw, Trophy } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { paperAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import AchievementsStrip from '../components/AchievementsStrip';

const Paper = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [trades, setTrades] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [form, setForm] = useState({ symbol: '', side: 'buy', qty: '' });
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [a, t, lb] = await Promise.all([paperAPI.account(), paperAPI.trades(20), paperAPI.leaderboard()]);
      setAccount(a);
      setTrades(t);
      setLeaderboard(lb);
    } catch (err) {
      // Best effort; we don't surface a toast because the page already shows
      // a metric strip with whatever data is available. Logging keeps it
      // discoverable in the console for debugging.
      console.warn('Paper account refresh failed:', err);
    }
  }, []);

  useEffect(() => { if (user) refresh(); }, [user, refresh]);

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 pt-16 text-center p-12">
        <h1 className="text-2xl font-bold mb-3">Paper Trading</h1>
        <Button className="bg-purple-600 hover:bg-purple-700" onClick={() => navigate('/auth')}>Sign in to start</Button>
      </div>
    );
  }

  const placeOrder = async () => {
    if (!form.symbol.trim() || !form.qty) {
      toast({ title: 'Missing fields', description: 'Symbol and qty required', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      await paperAPI.trade({ symbol: form.symbol.toUpperCase(), side: form.side, qty: Number(form.qty) });
      toast({ title: 'Order filled', description: `${form.side.toUpperCase()} ${form.qty} ${form.symbol.toUpperCase()}` });
      setForm({ symbol: '', side: 'buy', qty: '' });
      refresh();
    } catch (e) {
      toast({ title: 'Order rejected', description: e?.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!window.confirm('Reset paper account to $100,000? This deletes all trades.')) return;
    await paperAPI.reset();
    toast({ title: 'Account reset' });
    refresh();
  };

  const ret = account?.total_return_pct ?? 0;

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Wallet className="text-purple-600" size={28} /> Paper Trading
            </h1>
            <p className="text-gray-500">Practice with $100k virtual cash. Real-time prices.</p>
          </div>
          <Button variant="outline" onClick={reset} data-testid="paper-reset-btn"><RotateCcw size={14} className="mr-1" /> Reset</Button>
        </div>

        <AchievementsStrip />

        {/* Summary */}
        <div className="grid md:grid-cols-4 gap-3 mb-6">
          <Card><CardContent className="p-4"><p className="text-xs text-gray-500">Equity</p><p className="text-2xl font-bold" data-testid="paper-equity">${account?.equity?.toLocaleString() || '—'}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs text-gray-500">Cash</p><p className="text-2xl font-bold" data-testid="paper-cash">${account?.cash?.toLocaleString() || '—'}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs text-gray-500">Total Return</p><p className={`text-2xl font-bold ${ret >= 0 ? 'text-green-600' : 'text-red-600'}`} data-testid="paper-return">{ret >= 0 ? '+' : ''}{ret}%</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs text-gray-500">Positions</p><p className="text-2xl font-bold">{account?.positions?.length || 0}</p></CardContent></Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Order ticket */}
          <Card className="lg:col-span-1" data-testid="paper-order-card">
            <CardHeader><CardTitle className="text-lg">New Order</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-xs text-gray-600">Symbol</label>
                <Input value={form.symbol} onChange={(e) => setForm({ ...form, symbol: e.target.value.toUpperCase() })} placeholder="AAPL" data-testid="paper-symbol-input" />
              </div>
              <div>
                <label className="text-xs text-gray-600">Side</label>
                <select className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm h-10" value={form.side} onChange={(e) => setForm({ ...form, side: e.target.value })} data-testid="paper-side-select">
                  <option value="buy">Buy</option>
                  <option value="sell">Sell</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-600">Quantity</label>
                <Input type="number" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} data-testid="paper-qty-input" />
              </div>
              <Button onClick={placeOrder} disabled={busy} className="w-full bg-purple-600 hover:bg-purple-700" data-testid="paper-submit-btn">
                {busy ? 'Submitting…' : 'Submit Order'}
              </Button>
            </CardContent>
          </Card>

          {/* Holdings */}
          <Card className="lg:col-span-2" data-testid="paper-holdings-card">
            <CardHeader><CardTitle className="text-lg">Holdings</CardTitle></CardHeader>
            <CardContent>
              {!account?.positions?.length ? (
                <p className="text-gray-500 text-sm text-center py-6">No positions yet. Place your first order.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-left text-gray-600 border-b">
                    <tr>
                      <th className="pb-2">Symbol</th><th className="pb-2">Qty</th><th className="pb-2">Avg Cost</th><th className="pb-2">Price</th><th className="pb-2 text-right">P&L</th>
                    </tr>
                  </thead>
                  <tbody>
                    {account.positions.map((p) => (
                      <tr key={p.symbol} className="border-b hover:bg-gray-50" data-testid={`paper-position-${p.symbol}`}>
                        <td className="py-2 font-semibold">{p.symbol}</td>
                        <td className="py-2">{p.qty}</td>
                        <td className="py-2">${p.avg_cost}</td>
                        <td className="py-2">${p.price}</td>
                        <td className={`py-2 text-right font-medium ${p.unrealized_pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {p.unrealized_pnl >= 0 ? '+' : ''}${p.unrealized_pnl} ({p.unrealized_pnl_pct}%)
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Trades + Leaderboard */}
        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          <Card data-testid="paper-trades-card">
            <CardHeader><CardTitle className="text-lg">Recent Trades</CardTitle></CardHeader>
            <CardContent>
              {!trades.length ? <p className="text-gray-500 text-sm py-3">No trades yet.</p> : (
                <div className="space-y-1 max-h-80 overflow-y-auto">
                  {trades.map((t) => (
                    <div key={t.id} className="flex justify-between text-sm py-1 border-b last:border-0">
                      <div>
                        <Badge variant="outline" className={t.side === 'buy' ? 'border-green-500 text-green-700' : 'border-red-500 text-red-700'}>{t.side.toUpperCase()}</Badge>
                        <span className="ml-2 font-medium">{t.symbol}</span>
                        <span className="ml-2 text-gray-500">{t.qty} @ ${t.price}</span>
                      </div>
                      <span className="text-gray-400 text-xs">{new Date(t.executed_at).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          <Card data-testid="paper-leaderboard-card">
            <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Trophy size={18} className="text-yellow-500" /> Top Traders</CardTitle></CardHeader>
            <CardContent>
              {!leaderboard.length ? <p className="text-gray-500 text-sm py-3">No traders yet.</p> : (
                <div className="space-y-1">
                  {leaderboard.slice(0, 10).map((row, i) => (
                    <div key={row.user_id} className="flex justify-between items-center text-sm py-1.5 border-b last:border-0">
                      <span className="text-gray-700">#{i + 1} <span className="text-gray-400 font-mono text-xs">{row.user_id?.slice(0, 8)}</span></span>
                      <span className={`font-semibold ${row.return_pct >= 0 ? 'text-green-600' : 'text-red-600'}`}>{row.return_pct >= 0 ? '+' : ''}{row.return_pct}%</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Paper;
